import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { getProgress, getMe, getStudentObjectivesPerformance } from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Trophy, Target, Keyboard, BookOpen, 
  TrendingUp, Clock, CheckCircle, AlertTriangle
} from 'lucide-react';

const ProgressPage = () => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [objectives, setObjectives] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userRes, progressRes] = await Promise.all([
          getMe(),
          getProgress()
        ]);
        setUser(userRes.data);
        setProgress(progressRes.data);
        
        // Load objectives performance
        try {
          const objectivesRes = await getStudentObjectivesPerformance(userRes.data.user_id);
          setObjectives(objectivesRes.data);
        } catch (e) {
          console.log('Could not load objectives');
        }
      } catch {
        toast.error('Failed to load progress');
        navigate('/student');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const sessions = progress?.sessions || [];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-white/5 bg-background-paper/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/student')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-xl font-bold">My Progress</h1>
            <p className="text-text-secondary text-sm">{user?.name}</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="card-glass p-6" data-testid="stat-total">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Completed</p>
                <p className="font-heading text-2xl font-bold">{progress?.total_exercises || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="card-glass p-6" data-testid="stat-accuracy">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-status-success/20 flex items-center justify-center">
                <Target className="w-5 h-5 text-status-success" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Avg Accuracy</p>
                <p className="font-heading text-2xl font-bold">{progress?.avg_accuracy || 0}%</p>
              </div>
            </div>
          </Card>
          
          <Card className="card-glass p-6" data-testid="stat-wpm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-secondary/20 flex items-center justify-center">
                <Keyboard className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Avg WPM</p>
                <p className="font-heading text-2xl font-bold">{progress?.avg_wpm || 0}</p>
              </div>
            </div>
          </Card>
          
          <Card className="card-glass p-6" data-testid="stat-quiz">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-status-warning/20 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-status-warning" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Avg Quiz</p>
                <p className="font-heading text-2xl font-bold">{progress?.avg_quiz_score || 0}%</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Objectives Performance */}
        {objectives && objectives.objectives?.length > 0 && (
          <>
            <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              My ELA Objectives
              <span className="text-text-muted text-sm font-normal ml-2">
                ({objectives.summary?.mastered || 0}/{objectives.summary?.total_objectives || 0} mastered)
              </span>
            </h2>
            
            <div className="grid gap-3 mb-8">
              {objectives.objectives.map((obj, idx) => (
                <Card 
                  key={idx}
                  className={`card-glass p-4 border ${obj.mastered ? 'border-neon-green/30' : 'border-neon-yellow/30'}`}
                  data-testid={`objective-card-${idx}`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 flex-1">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${obj.mastered ? 'bg-neon-green/20' : 'bg-neon-yellow/20'}`}>
                        {obj.mastered ? (
                          <CheckCircle className="w-5 h-5 text-neon-green" />
                        ) : (
                          <AlertTriangle className="w-5 h-5 text-neon-yellow" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-primary font-bold text-sm">{obj.code}</span>
                          {obj.mastered ? (
                            <span className="px-2 py-0.5 rounded text-xs bg-neon-green/20 text-neon-green">Mastered!</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded text-xs bg-neon-yellow/20 text-neon-yellow">Keep Practicing</span>
                          )}
                        </div>
                        <p className="text-text-muted text-xs truncate">{obj.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-text-muted text-xs">Practiced</p>
                        <p className="font-mono font-bold">{obj.times_practiced}x</p>
                      </div>
                      <div className="text-center">
                        <p className="text-text-muted text-xs">Avg Quiz</p>
                        <p className={`font-mono text-lg font-bold ${obj.avg_quiz_score >= 80 ? 'text-neon-green' : obj.avg_quiz_score >= 60 ? 'text-neon-yellow' : 'text-neon-red'}`}>
                          {obj.avg_quiz_score}%
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* Session History */}
        <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          Exercise History
        </h2>

        {sessions.length === 0 ? (
          <Card className="card-glass p-12 text-center" data-testid="no-history">
            <Keyboard className="w-12 h-12 text-text-muted mx-auto mb-4" />
            <h3 className="font-heading text-lg font-bold mb-2">No exercises completed yet</h3>
            <p className="text-text-secondary mb-6">Complete exercises to see your progress here</p>
            <Button 
              className="btn-primary rounded-sm"
              onClick={() => navigate('/student')}
            >
              Go to Exercises
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {sessions.map((session, idx) => (
              <Card 
                key={session.session_id || idx}
                className="card-glass p-4"
                data-testid={`session-card-${idx}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                      <span className="font-mono text-primary font-bold">#{sessions.length - idx}</span>
                    </div>
                    <div>
                      <p className="font-medium">Exercise Session</p>
                      <p className="text-text-muted text-xs flex items-center gap-2">
                        <Clock className="w-3 h-3" />
                        {session.completed_at ? new Date(session.completed_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-text-muted text-xs">Accuracy</p>
                      <p className={`font-mono font-bold ${session.accuracy >= 80 ? 'text-status-success' : session.accuracy >= 60 ? 'text-status-warning' : 'text-status-error'}`}>
                        {session.accuracy}%
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-text-muted text-xs">WPM</p>
                      <p className="font-mono font-bold text-secondary">{session.actual_wpm}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-text-muted text-xs">Quiz</p>
                      <p className={`font-mono font-bold ${session.quiz_score >= 80 ? 'text-status-success' : session.quiz_score >= 60 ? 'text-status-warning' : 'text-status-error'}`}>
                        {session.quiz_score}%
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ProgressPage;
